import { useCallback, useEffect, useRef, useState } from 'react';

import { api, getHttpErrorStatus, logHttpError } from '../../services/api';
import { PET_SPECIES } from '../../pets/types';
import type { PetAction, PetSnapshot } from '../../pets/types';

interface PetCardProps {
  initialState?: PetSnapshot;
  onStateChange?: (state: PetSnapshot) => void;
}

type PendingOperation = PetAction | 'enable';

const needRows: Array<{ key: keyof NonNullable<PetSnapshot['needs']>; label: string }> = [
  { key: 'hunger', label: 'Сытость' },
  { key: 'mood', label: 'Настроение' },
  { key: 'energy', label: 'Энергия' },
];

const actionLabels: Record<PetAction, string> = {
  feed: 'Покормить',
  play: 'Поиграть',
  rest: 'Отдохнуть',
};

const formatCooldown = (seconds: number) => {
  const total = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const errorResponse = (error: unknown) => (
  error as { response?: { status?: number; data?: { detail?: unknown } } }
).response;

const PetCard = ({ initialState, onStateChange }: PetCardProps) => {
  const [state, setState] = useState<PetSnapshot | null>(initialState ?? null);
  const [loading, setLoading] = useState(initialState === undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cooldownFeedback, setCooldownFeedback] = useState<string | null>(null);
  const [pendingOperation, setPendingOperation] = useState<PendingOperation | null>(null);
  const pendingOperationRef = useRef<PendingOperation | null>(null);

  const loadState = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get<PetSnapshot>('/pets/me');
      setState(response.data);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки PET-системы', error);
      setLoadError('Не удалось загрузить питомца.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialState !== undefined) {
      setState(initialState);
      setLoading(false);
      setLoadError(null);
      return;
    }
    void loadState();
  }, [initialState, loadState]);

  const applyServerState = (nextState: PetSnapshot) => {
    setState(nextState);
    onStateChange?.(nextState);
  };

  const requestSnapshot = async (operation: PendingOperation, path: string) => {
    if (pendingOperationRef.current !== null) return;

    pendingOperationRef.current = operation;
    setPendingOperation(operation);
    setActionError(null);
    setCooldownFeedback(null);
    try {
      const response = await api.post<PetSnapshot>(path);
      applyServerState(response.data);
      setActionError(null);
      setCooldownFeedback(null);
    } catch (error: unknown) {
      logHttpError('Ошибка действия с питомцем', error);
      const response = errorResponse(error);
      const status = getHttpErrorStatus(error) ?? response?.status;
      const detail = response?.data?.detail;

      if (status === 409 && typeof detail === 'object' && detail !== null && 'remaining_seconds' in detail) {
        const remainingSeconds = (detail as { remaining_seconds?: unknown }).remaining_seconds;
        if (typeof remainingSeconds === 'number') {
          setCooldownFeedback(`Осталось: ${formatCooldown(remainingSeconds)}`);
          return;
        }
      }

      if (status === 422 && operation === 'play') {
        setActionError('Для игры нужно не менее 15 энергии.');
        return;
      }

      setActionError('Не удалось выполнить действие. Попробуйте ещё раз.');
    } finally {
      pendingOperationRef.current = null;
      setPendingOperation(null);
    }
  };

  if (loading && state === null) {
    return (
      <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border" aria-busy="true">
        <p role="status" className="shark-muted text-sm">Загрузка питомца...</p>
      </article>
    );
  }

  if (state === null) {
    return (
      <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border">
        <p role="alert" className="shark-error text-sm">{loadError ?? 'Не удалось загрузить питомца.'}</p>
        <button type="button" onClick={() => void loadState()} className="shark-accent-button shark-density-control mt-4 rounded-xl text-sm font-bold">
          Повторить
        </button>
      </article>
    );
  }

  const enable = () => void requestSnapshot('enable', '/pets/enable');

  if (!state.active) {
    return (
      <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border">
        <h2 className="text-lg font-bold text-white">Ваш питомец ждёт</h2>
        <p className="shark-muted mt-1 text-sm">Заведите питомца своего клана и заботьтесь о нём.</p>
        <button type="button" onClick={enable} disabled={pendingOperation !== null} className="shark-accent-button shark-density-control mt-4 rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50">
          {pendingOperation === 'enable' ? 'Создаём...' : 'Завести питомца'}
        </button>
        {actionError && <p role="alert" className="shark-error mt-3 text-sm">{actionError}</p>}
      </article>
    );
  }

  if (!state.enabled) {
    return (
      <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border">
        <h2 className="text-lg font-bold text-white">Питомец на паузе</h2>
        <p className="shark-muted mt-1 text-sm">Прогресс питомца заморожен. Продолжите, когда будете готовы снова заботиться о нём.</p>
        <button type="button" onClick={enable} disabled={pendingOperation !== null} className="shark-accent-button shark-density-control mt-4 rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50">
          {pendingOperation === 'enable' ? 'Продолжаем...' : 'Продолжить'}
        </button>
        {actionError && <p role="alert" className="shark-error mt-3 text-sm">{actionError}</p>}
      </article>
    );
  }

  const species = state.species ? PET_SPECIES[state.species] : null;
  const needs = state.needs;

  if (!species || !needs) {
    return (
      <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border">
        <p role="alert" className="shark-error text-sm">Не удалось прочитать состояние питомца.</p>
      </article>
    );
  }

  return (
    <article data-testid="pet-card" className="shark-card shark-border w-full max-w-none rounded-2xl border" aria-busy={pendingOperation !== null}>
      <header className="flex items-start gap-3">
        <span aria-hidden="true" className="text-3xl">{species.emoji}</span>
        <div>
          <h2 className="text-lg font-bold text-white">{species.label}</h2>
          <p className={`${species.accent} mt-1 text-sm font-bold`}>Питомец вашего клана</p>
        </div>
      </header>

      <div className="mt-5 space-y-3">
        {needRows.map(({ key, label }) => {
          const value = Math.min(100, Math.max(0, needs[key]));
          return (
            <div key={key}>
              <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                <span className="shark-muted">{label}</span>
                <span className="font-bold text-white">{value}</span>
              </div>
              <div
                role="progressbar"
                aria-label={label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={value}
                aria-valuetext={`${value} из 100`}
                className="h-2 w-full overflow-hidden rounded-full bg-gray-700"
              >
                <div className="h-full rounded-full bg-blue-600" style={{ width: `${value}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {(Object.keys(actionLabels) as PetAction[]).map((action) => (
          <button
            key={action}
            type="button"
            onClick={() => void requestSnapshot(action, `/pets/actions/${action}`)}
            disabled={pendingOperation !== null}
            aria-label={actionLabels[action]}
            className="shark-accent-button shark-density-control rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pendingOperation === action ? 'Сохраняем...' : actionLabels[action]}
          </button>
        ))}
      </div>

      {cooldownFeedback && <p role="status" className="shark-muted mt-3 text-sm">{cooldownFeedback}</p>}
      {actionError && <p role="alert" className="shark-error mt-3 text-sm">{actionError}</p>}
    </article>
  );
};

export default PetCard;
